import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FiAlertCircle, FiBarChart2, FiHelpCircle } from 'react-icons/fi'
import { NumberFormatValues } from 'react-number-format'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { FormHandles } from '@unform/core'
import { Alert } from '@vtex/shoreline'
import { AxiosError } from 'axios'

import { ButtonsCancelSave } from '~/components/Buttons'
import Panel from '~/components/Panel'
import Tooltip from '~/components/Tooltip'
import { Badge } from '~/components/UI/Badge'
import { InputMoney } from '~/components/unform'
import Form from '~/components/unform/Form'
import {
  formatMoney,
  formatMoneyV2,
  getDurationInDays,
  isAdvertiser
} from '~/helpers'
import { useAppInfo } from '~/hooks/useAppInfo'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import { useAppSelector } from '~/store/hooks'

import { canShowSpendReportLink } from '../../helpers/canShowSpendReportLink'
import { useCampaignReadOnly } from '../../hooks/useCampaignReadOnly'
import { getBudgetPacingImpactService } from '../../services/campaign/budget-pacing-impact'
import {
  getCampaignPermissionsService,
  getCampaignPermissionsServiceKey
} from '../../services/configuration/campaign-permissions'
import { getMinCosts } from '../../store/industry/budget/actions'
import { patch } from '../../store/industry/campaign/actions'
import BudgetTypeScope from '../CampaignForm/elements/BudgetTypeScope'
import FormEditDailyCost from '../DailyCost/FormEditDailyCost'
import EditButton from '../EditButton'
import BudgetPacingCyclePreview from './BudgetPacingCyclePreview'
import BudgetPacingRecalcNotice from './BudgetPacingRecalcNotice'
import { DailyBudget } from './DailyBudget'
import { applyCostSettingsByAdType, parseCurrencyValue } from './helper'
import PanelIconLeft from './PanelIconLeft'

import * as S from './styles'

const INVESTMENT_OBJECTIVE_LABEL_KEY = {
  traffic: 'rm:campaign.create.InvestmentObjective.traffic.label',
  conversion: 'rm:campaign.create.InvestmentObjective.conversion.label'
} as const

const labelForInvestmentObjective = (value: unknown) => {
  if (typeof value !== 'string') return null
  if (!Object.hasOwn(INVESTMENT_OBJECTIVE_LABEL_KEY, value)) return null
  if (value !== 'traffic' && value !== 'conversion') return null

  return INVESTMENT_OBJECTIVE_LABEL_KEY[value]
}

const costValueIsValid = (value: string | number) =>
  value !== undefined && value !== null

const PanelCampaignBudget = ({ data, loading }: PanelCampaignBudgetProps) => {
  const dispatch = useDispatch()
  const formRef = useRef<FormHandles>(null)

  const { t } = useAPPTranslation()
  const { lengthCentsCurrency } = useAppInfo()

  const [isEditMode, setIsEditMode] = useState(false)
  const [dailyBudgetInput, setDailyBudgetInput] = useState<number | null>(null)
  const [debouncedDailyBudget, setDebouncedDailyBudget] = useState<
    number | null
  >(null)

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedDailyBudget(dailyBudgetInput),
      500
    )
    return () => clearTimeout(timer)
  }, [dailyBudgetInput])

  useEffect(() => {
    if (!isEditMode) {
      setDailyBudgetInput(null)
      setDebouncedDailyBudget(null)
    }
  }, [isEditMode])

  /**
   * <Campaign infos>
   */

  const isReadOnlyView = useCampaignReadOnly()

  const canEdit = useMemo(
    () => isAdvertiser() && data?.canEditCampaign && !isReadOnlyView,
    [data?.canEditCampaign, isReadOnlyView]
  )

  const productsId = useMemo(
    () => data?.ads?.data?.map(item => ({ id: item.product_id })),
    [data]
  )

  const ads = useMemo(() => data?.ads?.data, [data])

  const campaignCosts = useMemo(
    () => ({
      cpc: data?.settings?.cpc,
      cpm: data?.settings?.cpm
    }),
    [data?.settings]
  )

  const isOffsite = data?.type === 'off_site'
  const investmentObjectiveLabel = labelForInvestmentObjective(
    data?.settings?.investment_objective_type
  )
  /**
   * </Campaign infos>
   */

  /**
   * Handle campaign permissions
   */
  const queryKeyCampaignPermissions = [
    getCampaignPermissionsServiceKey,
    { publisher_id: data.publisher.id }
  ]

  const { data: campaignPermissions } = useQuery({
    queryKey: queryKeyCampaignPermissions,
    queryFn: async () => {
      const response = await getCampaignPermissionsService({
        publisher_id: data.publisher.id
      })

      return response?.data
    }
  })

  const budgetPacingEnabled = !!campaignPermissions?.allow_budget_pacing
  const pacingImpactAvailable =
    budgetPacingEnabled && data?.raw?.budget_pacing_active !== false

  const {
    data: pacingImpact,
    isFetching: pacingImpactLoading,
    isError: pacingImpactError,
    refetch: retryPacingImpact
  } = useQuery({
    enabled:
      pacingImpactAvailable &&
      debouncedDailyBudget != null &&
      isEditMode &&
      debouncedDailyBudget !== Number(data.settings.daily_budget),
    queryKey: [
      'budget-pacing-impact',
      { campaignId: data.id, dailyBudget: debouncedDailyBudget }
    ],
    queryFn: async () => {
      const response = await getBudgetPacingImpactService(
        data.id,
        debouncedDailyBudget as number
      )
      return response?.data
    },
    retry: (failureCount, error) => {
      const status = (error as AxiosError).response?.status
      const isPermanentClientError =
        status !== undefined &&
        status >= 400 &&
        status < 500 &&
        status !== 408 &&
        status !== 429

      return !isPermanentClientError && failureCount < 3
    },
    retryDelay: 1000,
    staleTime: 0,
    gcTime: 0
  })

  const hasPendingDailyBudgetChange =
    dailyBudgetInput != null &&
    dailyBudgetInput !== Number(data.settings.daily_budget)

  const showPacingImpact = Boolean(
    pacingImpactAvailable && isEditMode && hasPendingDailyBudgetChange
  )

  const pacingImpactIsLoading =
    dailyBudgetInput !== debouncedDailyBudget || pacingImpactLoading

  /**
   * </Campaign permissions>
   */
  const {
    budget: {
      minCosts: { data: minCostsData, loading: minCostsLoading }
    },
    campaign: {
      patch: { loading: loadingSave }
    }
  } = useAppSelector(state => state.retailMedia.industry)

  /**
   * <Get min costs v1>
   */
  const shouldGetMinCosts = useMemo(() => {
    const productCheck = data.adType === 'product' && data.publisher
    const othersCheck = data.adType !== 'product' && data.id

    return productCheck || othersCheck
  }, [data.adType, data.id, data.publisher])

  const payload = useMemo(() => {
    if (!shouldGetMinCosts) return null

    const productAdsBody = {
      publisher_id: data.publisher.id,
      products: productsId
    }

    return {
      body: productAdsBody,
      campaignId: data.id,
      adType: data.adType
    } as GetMinCostActionsProps
  }, [
    data?.adType,
    data?.id,
    data?.publisher?.id,
    productsId,
    shouldGetMinCosts
  ])

  useEffect(() => {
    if (payload) {
      dispatch(getMinCosts({ ...payload }))
    }
  }, [payload, dispatch])
  /**
   * </Get min costs v1>
   */

  /**
   * <Get min costs v2>
   */

  // const { data: costV2 } = useQuery({
  //   enabled: !!data?.id && data.adType !== 'product',
  //   queryKey: ['minCosts', { publisher_id: data?.publisher?.id }],
  //   queryFn: async () => await getMinCostsByCampaignService(data.id)
  // })

  /**
   * </Get min costs v2>
   */

  /**
   * Tratando min costs
   * Estamos tratando duas fontes de costs até termos um endpoint único para isso.
   * Veremos pós BF 2025
   */

  const globalMinCost = useMemo(
    () => ({
      cpc: minCostsData?.global?.min_cpc,
      cpm: minCostsData?.global?.min_cpm
    }),
    [minCostsData?.global]
  )

  /**
   * <Handle CPC>
   * No CPC conseguimos fazer uma validação por anúncios, pois cada anúncio
   * pode ter um valor mínimo diferente de acordo com a categeoria do produto.
   */
  const adsBelowMinCostLength = useMemo(() => {
    if (!ads) return 0

    type AdWithoutCPCWithMinCost = AdsResultFormatted & {
      minCpc: string | number
    }

    const adsWithoutCPCWithMinCost = ads?.map(item => {
      const minCost = minCostsData?.ads?.find(
        minCostItem => minCostItem.id === item.product_id
      )

      return { ...item, minCpc: minCost?.min_cpc }
    })

    const filterAdsBelowMinCost = (item: AdWithoutCPCWithMinCost) => {
      const minCPC = item?.minCpc || globalMinCost.cpc
      const currentCPC = item?.settings?.cpc || campaignCosts.cpc

      return Number(currentCPC) < Number(minCPC)
    }

    const adsBelowMinLength = adsWithoutCPCWithMinCost.filter(
      filterAdsBelowMinCost
    ).length

    return adsBelowMinLength
  }, [ads, campaignCosts?.cpc, globalMinCost.cpc, minCostsData?.ads])

  const costIsValid = useMemo(() => {
    return (
      adsBelowMinCostLength === 0 ||
      (campaignCosts.cpc ?? 1) >= (globalMinCost.cpc ?? 0)
    )
  }, [adsBelowMinCostLength, campaignCosts.cpc, globalMinCost.cpc])
  /*** </Handle CPC> */

  /**
   * <Handle CPM>
   */
  const adsBelowMinCostCpmLength = useMemo(() => {
    const ads = data?.ads?.data
    if (ads) {
      const adsWithoutCPCWithMinCost = ads?.map(item => {
        const minCost = minCostsData?.ads?.find(
          minCostItem => minCostItem.id === item.product_id
        )
        return { ...item, minCpm: minCost?.min_cpm }
      })

      adsWithoutCPCWithMinCost.filter(
        item =>
          Number(item?.settings?.cpm) < Number(minCostsData?.global?.min_cpm)
      )

      return adsWithoutCPCWithMinCost.filter(item => {
        const minCPM = item?.minCpm || minCostsData?.global?.min_cpm
        const currentCPM = item?.settings?.cpm || data?.settings?.cpm

        return Number(currentCPM) < Number(minCPM)
      }).length
    }
    return 0
  }, [data, minCostsData])

  const costCpmIsValid = useMemo(() => {
    return (
      adsBelowMinCostCpmLength === 0 ||
      (campaignCosts.cpm ?? 1) >= (globalMinCost.cpm ?? 0)
    )
  }, [adsBelowMinCostCpmLength, campaignCosts.cpm, globalMinCost.cpm])
  /*** </Handle CPM> */

  /**
   * <HandleSubmit>
   */
  const queryClient = useQueryClient()

  const handleSuccessSubmit = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['get-ads']
    })

    setIsEditMode(false)
  }, [queryClient])

  const handleSubmit = useCallback(
    (formData: FormDataBudgetUpdate) => {
      const parseValue = (value: string) => {
        return parseCurrencyValue(value, lengthCentsCurrency)
      }

      const valueDailyBudget = formData.budget?.daily?.numAsString
      const valueCPC = formData.budget?.cpc?.numAsString
      const valueCPM = formData.budget?.cpm?.numAsString

      const values = applyCostSettingsByAdType(
        data?.adType,
        valueCPC,
        valueCPM,
        lengthCentsCurrency
      )

      const settings: SubmitSettings = {
        budget_type: formData?.settings?.budget_type || 'fast_as_possible',
        daily_budget: parseValue(valueDailyBudget),
        ...values
      }

      dispatch(
        patch({
          body: {
            settings,
            total_budget: parseValue(formData.budget?.total?.numAsString)
          },
          id: data?.id,
          successFeedbackMsg: t('rm:budget.updated.success.message'),
          onSuccess: handleSuccessSubmit,
          values: {
            patchKey: 'budget'
          }
        })
      )
    },
    [data, dispatch, handleSuccessSubmit, lengthCentsCurrency, t]
  )
  /*** <HandleSubmit> */

  /**
   * <Handle cost>
   * Função para manipular os valores de orçamento total, diário, cpc e cpm
   */
  const [totalBudget, setTotalBudget] = useState(0)

  type HandleCostTypes = 'total'
  type HandleCostHandler = {
    setter: React.Dispatch<React.SetStateAction<number>>
    min?: number
    setterIsValid?: React.Dispatch<React.SetStateAction<boolean>>
  }
  type HandleCostHandlers = {
    [key in HandleCostTypes]: HandleCostHandler
  }

  const handleCost = useCallback(
    (inputData: NumberFormatValues, type: HandleCostTypes) => {
      const amount = inputData.value
      const value = lengthCentsCurrency ? Number(amount) / 100 : Number(amount)

      const handlers: HandleCostHandlers = {
        total: {
          setter: setTotalBudget
        }
      }

      const setter = handlers?.[type]?.setter
      const setterIsValid = handlers?.[type]?.setterIsValid
      const min = handlers?.[type]?.min

      setter(value)

      if (setterIsValid && min) {
        setterIsValid(value >= min)
      }
    },
    [lengthCentsCurrency]
  )
  /** </Handle cost> */

  const handleDailyBudgetChange = useCallback(
    (inputData: NumberFormatValues) => {
      const amount = inputData.value
      if (amount === '' || amount == null) {
        setDailyBudgetInput(null)
        return
      }

      const value = lengthCentsCurrency ? Number(amount) / 100 : Number(amount)
      setDailyBudgetInput(Number.isNaN(value) ? null : value)
    },
    [lengthCentsCurrency]
  )

  /**
   * <Handle total budget>
   * Função para manipular o orçamento total baseado no orçamento diário
   * e o tempo de duração da campanha.
   */
  const campaignPeriod = useMemo(() => {
    const isAlwaysOn = !data.endDate
    // today dd/mm/yyyy

    const startDate = new Date()
    const endDateRaw = data.raw.end_at
    const endDate = isAlwaysOn ? null : endDateRaw

    return {
      startDate,
      endDate,
      isAlwaysOn,
      duration: getDurationInDays({
        startDate,
        endDate,
        returnType: 'number'
      }) as number
    }
  }, [data])

  const handleSuggestedDailyBudget = useCallback(() => {
    if (campaignPeriod?.endDate) {
      const duration = campaignPeriod?.duration || 1
      const totalBudgetValue = totalBudget / duration
      const formattedValue = formatMoneyV2({ value: totalBudgetValue })
      formRef.current?.setFieldValue('budget.daily', formattedValue)
    }
  }, [campaignPeriod?.endDate, campaignPeriod?.duration, totalBudget, formRef])

  useEffect(() => {
    if (totalBudget && campaignPeriod?.endDate) {
      handleSuggestedDailyBudget()
    }
  }, [campaignPeriod?.endDate, handleSuggestedDailyBudget, totalBudget])
  /** </Handle total budget> */

  const initialData = useMemo(
    () => ({
      budget: {
        daily: data?.settings?.daily_budget,
        cpc: data?.settings?.cpc,
        cpm: data?.settings?.cpm,
        total: data?.settings?.total_budget
      },
      settings: {
        budget_type: data?.settings?.budget_type
      }
    }),
    [data]
  )

  /**
   * Memos
   */

  const showAlertAdsBelowMinCostCPC = useMemo(
    () => !costIsValid && !!adsBelowMinCostLength && data.adType === 'product',
    [adsBelowMinCostLength, costIsValid, data.adType]
  )

  const showAlertAdsBelowMinCostCPM = useMemo(
    () =>
      !costCpmIsValid && !!adsBelowMinCostCpmLength && data.adType === 'banner',
    [adsBelowMinCostCpmLength, costCpmIsValid, data.adType]
  )

  const showTotalBudget = useMemo(
    () =>
      data?.raw?.total_budget !== undefined &&
      (campaignPermissions?.allow_total_budget_campaign ||
        Number(data?.raw?.total_budget) > 0),
    [campaignPermissions?.allow_total_budget_campaign, data?.raw?.total_budget]
  )

  const showSpendReportLink = canShowSpendReportLink(
    campaignPermissions?.allow_budget_pacing,
    data?.raw?.budget_pacing_active
  )

  const showBudgetType =
    !isOffsite && !data?.settings?.daily_cost && !!data?.settings?.budget_type

  const showCostGroup =
    costValueIsValid(data?.settings?.daily_budget) ||
    costValueIsValid(data?.settings?.cpc) ||
    costValueIsValid(data?.settings?.cpm) ||
    showBudgetType

  return (
    <Panel
      title={t('rm:investment')}
      isLoading={loading}
      iconLeft={
        <PanelIconLeft
          costCpmIsValid={costCpmIsValid}
          adsBelowMinCostCpmLength={adsBelowMinCostCpmLength}
          costIsValid={costIsValid}
          adsBelowMinCostLength={adsBelowMinCostLength}
          adType={data.adType}
        />
      }
      template={isEditMode ? 'borderWarning' : 'shoreline'}
      headerSideContent={
        <div className="d-flex align-items-center gap-3">
          {showSpendReportLink && (
            <Link
              to={`/retail-media/campaign/view/${data?.id}/spend-reports?from=detail`}
              className="d-flex align-items-center gap-2 text-decoration-none fw-semibold text-dark"
            >
              <FiBarChart2 />
              {t('rm:spendReport.viewLink')}
            </Link>
          )}
          {canEdit && (
            <EditButton
              onClick={() => setIsEditMode((e: boolean) => !e)}
              isActive={isEditMode}
            />
          )}
        </div>
      }
    >
      {costValueIsValid(data?.settings?.daily_cost) && (
        <S.Wrapper
          className={
            isEditMode ? 'flex-grow-1 border-0 border-top px-0 pt-3' : ''
          }
        >
          {!isEditMode && (
            <S.Text>
              {t('rm:settings.daily_cost.title')}
              <Tooltip
                icon={<FiHelpCircle />}
                text={t('rm:settings.daily_cost.view.description')}
              />
            </S.Text>
          )}

          {!isEditMode && <p>{formatMoney(data?.settings?.daily_cost)}</p>}

          {isEditMode && <FormEditDailyCost data={data} />}
        </S.Wrapper>
      )}

      <Form
        onSubmit={handleSubmit}
        initialData={initialData}
        className="form-default"
        ref={formRef}
      >
        {showAlertAdsBelowMinCostCPC && (
          <Alert variant="warning">
            {t('rm:adsBelowMinCostCPC.alert', {
              quantity: adsBelowMinCostLength
            })}
          </Alert>
        )}

        {showAlertAdsBelowMinCostCPM && (
          <Alert variant="warning">
            {t('rm:adsBelowMinCostCPM.alert', {
              quantity: adsBelowMinCostCpmLength
            })}
          </Alert>
        )}

        {(showTotalBudget || (isOffsite && investmentObjectiveLabel)) && (
          <S.RowWrapper>
            <S.PairRow>
              {showTotalBudget && (
                <S.Wrapper>
                  <S.Text>{t('rm:totalBudget')}</S.Text>
                  {!isEditMode && (
                    <p>{formatMoney(data.raw.total_budget || 0)}</p>
                  )}
                  {isEditMode && (
                    <InputMoney
                      id="budget.total"
                      name="budget.total"
                      loading={minCostsLoading}
                      onChange={data => handleCost(data, 'total')}
                      disabled={
                        !campaignPermissions?.allow_total_budget_campaign
                      }
                    />
                  )}
                </S.Wrapper>
              )}

              {isOffsite && investmentObjectiveLabel && (
                <S.Wrapper>
                  <S.Text>
                    {t('rm:campaign.create.ReviewStep.objective')}
                  </S.Text>
                  <p>{t(investmentObjectiveLabel)}</p>
                </S.Wrapper>
              )}
            </S.PairRow>
          </S.RowWrapper>
        )}

        {showCostGroup && (
          <S.RowWrapper className="hasBorder">
            <S.GroupWrapper>
              {costValueIsValid(data?.settings?.daily_budget) && (
                <DailyBudget
                  data={data}
                  inputName="budget.daily"
                  isEditMode={isEditMode}
                  isLoading={minCostsLoading}
                  budgetPacingEnabled={budgetPacingEnabled}
                  spendReportsTo={
                    showSpendReportLink
                      ? `/retail-media/campaign/view/${data?.id}/spend-reports?from=detail`
                      : undefined
                  }
                  onDailyBudgetChange={handleDailyBudgetChange}
                />
              )}

              {costValueIsValid(data?.settings?.cpc) && (
                <S.Wrapper>
                  <S.Text>
                    {t('rm:cpc.title')}
                    <Tooltip
                      icon={<FiHelpCircle />}
                      text={t('rm:metrics.cpc.tooltip')}
                    />
                  </S.Text>

                  {!isEditMode && (
                    <p className="cpc-info">
                      {formatMoney(data?.settings?.cpc)}{' '}
                      {!costIsValid && !isEditMode && (
                        <FiAlertCircle className="budget-alert" />
                      )}
                    </p>
                  )}

                  {isEditMode && (
                    <InputMoney
                      id="budget.cpc"
                      name="budget.cpc"
                      required
                      loading={minCostsLoading}
                    />
                  )}

                  {!!minCostsData?.global?.min_cpc && !costIsValid && (
                    <small className="budget-alert">
                      {t('rm:minimumValue')}{' '}
                      {formatMoney(minCostsData.global.min_cpc)}
                    </small>
                  )}
                </S.Wrapper>
              )}

              {costValueIsValid(data?.settings?.cpm) && (
                <S.Wrapper>
                  <S.Text>
                    {t('rm:cpm.title')}
                    <Tooltip
                      icon={<FiHelpCircle />}
                      text={t('rm:metrics.cpm.tooltip')}
                    />
                  </S.Text>
                  {!isEditMode && <p>{formatMoney(data?.settings?.cpm)}</p>}
                  {isEditMode && (
                    <InputMoney
                      id="budget.cpm"
                      name="budget.cpm"
                      required
                      loading={minCostsLoading}
                    />
                  )}

                  {minCostsData?.global?.min_cpm && !costCpmIsValid && (
                    <small className={!costCpmIsValid && 'budget-alert'}>
                      {t('rm:minimumValue')}{' '}
                      {formatMoney(minCostsData.global.min_cpm)}
                    </small>
                  )}
                </S.Wrapper>
              )}

              {showBudgetType && (
                <S.Wrapper
                  className={
                    isEditMode
                      ? 'flex-grow-1 border-0 border-top px-0 pt-3'
                      : ''
                  }
                >
                  {!isEditMode && (
                    <S.Text>
                      {t('rm:settings.budget_type.title')}
                      <Tooltip
                        icon={<FiHelpCircle />}
                        text={t('rm:settings.budget_type.view.description')}
                      />
                    </S.Text>
                  )}

                  {!isEditMode && (
                    <Badge template="success">
                      {t(
                        `rm:settings.budget_type.values.${data.settings.budget_type}.long`
                      )}
                    </Badge>
                  )}
                  {isEditMode && <BudgetTypeScope />}
                </S.Wrapper>
              )}
            </S.GroupWrapper>
          </S.RowWrapper>
        )}

        {showPacingImpact && (
          <div className="mt-3">
            <div className="mb-2">
              <BudgetPacingRecalcNotice />
            </div>
            <BudgetPacingCyclePreview
              loading={pacingImpactIsLoading}
              error={pacingImpactError}
              onRetry={retryPacingImpact}
              remainingCycleAllocation={
                pacingImpact?.remaining_cycle_allocation
              }
              newDailyBudget={pacingImpact?.new_daily_budget}
              remainingDays={pacingImpact?.remaining_days}
            />
          </div>
        )}

        {isEditMode && canEdit && (
          <ButtonsCancelSave
            onCancelClick={() => setIsEditMode(false)}
            isLoading={loadingSave}
            isSaveLoading={showPacingImpact && pacingImpactIsLoading}
            isDisabled={showPacingImpact && pacingImpactIsLoading}
            showSavingChangesLoading
          />
        )}
      </Form>
    </Panel>
  )
}

export default PanelCampaignBudget
